import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { User as UserIcon, Settings, LogOut, Crown, Coins, Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { apiRequest } from "@/lib/queryClient";

interface UserData {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  planCode?: string;
  role?: string;
  avatar?: string;
  authorProfileImage?: string;
}

interface CreditStats {
  balance: number;
  totalEarned: number;
  totalSpent: number;
}

interface HeaderProps {
  onToggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
  onMobileMenuToggle?: () => void;
  mobileMenuOpen?: boolean;
  disableQueries?: boolean; // New prop to disable auth/credits queries
}

export function Header({ onToggleSidebar, sidebarCollapsed, onMobileMenuToggle, mobileMenuOpen, disableQueries = false }: HeaderProps) {
  const { data } = useQuery<{ user: UserData }>({
    queryKey: ["/api/auth/me"],
    enabled: !disableQueries, // Disable when requested
    retry: false
  });

  // Normalize for easier use
  const user = data?.user;

  const { data: creditStats } = useQuery<CreditStats>({
    queryKey: ["/api/credits/stats"],
    enabled: !disableQueries, // Disable when requested
    retry: false
  });

  const handleLogout = async () => {
    try {
      await apiRequest("/api/auth/logout", "POST");
      window.location.href = "/auth";
    } catch (error) {
      console.error("Logout failed:", error);
      // Still redirect even if logout fails to prevent stuck state
      window.location.href = "/auth";
    }
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName) return "U";
    const first = firstName.charAt(0).toUpperCase();
    const last = lastName ? lastName.charAt(0).toUpperCase() : "";
    return first + last;
  };

  const getPlanColor = (planCode?: string) => {
    switch (planCode) {
      case "FREE": return "bg-gray-100 text-gray-800";
      case "PRO": return "bg-blue-100 text-blue-800";
      case "ENTERPRISE": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <header className="h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex items-center justify-between h-full px-4 sm:px-6">
        {/* Left side - Mobile menu toggle and desktop sidebar toggle */}
        <div className="flex items-center gap-4">
          {/* Mobile menu toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden p-2"
            onClick={onMobileMenuToggle}
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          {/* Desktop sidebar toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="hidden lg:block p-2"
            onClick={onToggleSidebar}
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          <div className="hidden lg:block">
            <h2 className="text-lg font-semibold">Welcome back{user?.firstName ? `, ${user.firstName}` : ''}</h2>
            <p className="text-sm text-muted-foreground">Create amazing stories with AI assistance</p>
          </div>
        </div>

        {/* Right side - Credits and user menu */}
        <div className="flex items-center gap-4">
          {/* Credits display */}
          {user && (
            <div className="flex items-center gap-2 px-3 py-1 bg-secondary rounded-full">
              <Coins className="h-4 w-4 text-yellow-600" />
              <span className="text-sm font-medium">
                {creditStats?.balance || 0} credits
              </span>
            </div>
          )}

          {/* User menu */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
                  <div className="w-10 h-10 rounded-full overflow-hidden">
                    {user.authorProfileImage || user.avatar ? (
                      <img
                        src={user.authorProfileImage || user.avatar}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-300 text-white font-medium">
                        {getInitials(user.firstName, user.lastName)}
                      </div>
                    )}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user.email}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={getPlanColor(user.planCode)}>
                        {user.planCode || "FREE"}
                      </Badge>
                      {user.role === "admin" && (
                        <Badge variant="destructive">Admin</Badge>
                      )}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                <Link to="/profile">
                  <DropdownMenuItem className="cursor-pointer">
                    <UserIcon className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                </Link>
                
                <Link to="/settings">
                  <DropdownMenuItem className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                </Link>
                
                <Link to="/upgrade">
                  <DropdownMenuItem className="cursor-pointer">
                    <Crown className="mr-2 h-4 w-4" />
                    <span>Upgrade Plan</span>
                  </DropdownMenuItem>
                </Link>
                
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="cursor-pointer text-red-600 focus:text-red-600"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth">
              <Button>Sign In</Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
